import { useCallback, useMemo, useState } from 'react';

import { SCRIPT_STATUS_ATTRIBUTE } from './constants';
import { loadScript } from './loadScript';
import { EScriptStatus, IUseScript } from './model';

export const useScript = (src: string): IUseScript => {
  const [status, setStatus] = useState<EScriptStatus>(isSourceCorrect(src));

  const load = useCallback(async () => {
    //check if src value is invalid set idle status
    if (!src) {
      setStatus(EScriptStatus.IDLE);
    }

    // Fetch existing script element by src
    // It may have been added by another instance of this hook
    const script: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);

    if (script) {
      // Grab existing script status from attribute and set to state.
      const scriptStatus = script.getAttribute(SCRIPT_STATUS_ATTRIBUTE);

      if (scriptStatus && Object.values<string>(EScriptStatus).includes(scriptStatus)) {
        setStatus(getScriptStateEnumValue(scriptStatus));
      }
    } else {
      //if script was not loaded by another instance call load script function
      //pass handles for onload and onerror
      await loadScript(
        src,
        (scriptElement) => {
          //set ready status for async load
          setStatus(EScriptStatus.READY);
          //set ready attribute value for other instance
          setAttribute(scriptElement, EScriptStatus.READY);
        },
        (scriptElement) => {
          //set error status for async load
          setStatus(EScriptStatus.ERROR);
          //set error attribute value for other instance
          setAttribute(scriptElement, EScriptStatus.ERROR);
        },
      );
    }
  }, [src]);

  //return memoized result and load function
  return useMemo(() => ({ status, load }), [status, load]);
};

const setAttribute = (script: HTMLScriptElement, value: EScriptStatus) => {
  script.setAttribute(SCRIPT_STATUS_ATTRIBUTE, value);
};

const isSourceCorrect = (condition: string): EScriptStatus.LOADING | EScriptStatus.IDLE =>
  condition ? EScriptStatus.LOADING : EScriptStatus.IDLE;

const getScriptStateEnumValue = (value: string): EScriptStatus => {
  const enumValue = Object.keys(EScriptStatus).find(
    (o) => EScriptStatus[o as keyof typeof EScriptStatus].toString() === value,
  );
  return enumValue ? EScriptStatus[enumValue as keyof typeof EScriptStatus] : EScriptStatus.ERROR;
};
